import { Injectable, Optional } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { FormMetadataExtractorService, FormMetadata } from './form-metadata-extractor.service';
import { FormService } from '@nrwl/rx-service-forms';
import { SnackbarService } from '@nrwl/snackbar-services';

export interface FormSavePayload {
  id: number;
  data: {
    form_data: any;
    metadata?: FormMetadata;
    status: any;
  };
}

export interface FormSaveOptions {
  status?: number;
  bulkIds?: number[];
  skipMetadata?: boolean;
  storeContext?: 'library' | 'inbox';
  useDirectApi?: boolean; // New flag to bypass NgRx
}

@Injectable({
  providedIn: 'root',
})
export class FormSaveService {
  constructor(
    private formMetadataExtractorService: FormMetadataExtractorService,
    private formService: FormService,
    private snackbarService: SnackbarService,
    @Optional() private store?: Store, // Make store optional
  ) {}
  saveForm(
    formId: number,
    formData: any,
    presenterComponent?: any,
    options: FormSaveOptions = {},
  ): Observable<any> {
    let payload: FormSavePayload = {
      id: formId,
      data: {
        form_data: formData,
        status: 2,
      },
    };

    if (presenterComponent && !options.skipMetadata) {
      payload = this.formMetadataExtractorService.addMetadataToPayload(
        payload,
        presenterComponent,
      ) as FormSavePayload;
    }

    if (options.useDirectApi || !this.store) {
      return this.updateFormDirectly(payload, options);
    }

    this.dispatchSaveAction(payload, options);
    return of({ success: true, payload });
  }

  saveFormWithStatus(
    formId: number,
    formData: any,
    status: number,
    presenterComponent?: any,
    bulkIds?: number[],
  ): Observable<any> {
    const options: FormSaveOptions = {
      status,
      bulkIds,
    };

    return this.saveForm(formId, formData, presenterComponent, options);
  }

  private updateFormDirectly(payload: FormSavePayload, options: FormSaveOptions): Observable<any> {
    // Direct API update without NgRx
    const updateData = {
      ...payload.data,
      status: options.status,
    };

    return this.formService.update(payload.id, updateData).pipe(
      map((response) => {
        this.snackbarService.success('Form saved successfully');

        if (options.status !== undefined && options.bulkIds && options.bulkIds.length > 0) {
          const bulkUpdates = options.bulkIds.map((id) =>
            this.formService.update(id, { status: options.status }).pipe(
              catchError((err) => {
                return of(null);
              }),
            ),
          );

          forkJoin(bulkUpdates).subscribe(
            (results) => console.log('✌️ FormSaveService: Bulk status update completed', results),
            (error) => console.error('✌️ FormSaveService: Bulk status update error', error),
          );
        }

        return { success: true, response, payload };
      }),
      catchError((error) => {
        this.snackbarService.show('Failed to save form', 'error', 3000);
        return of({ success: false, error, payload });
      }),
    );
  }

  private dispatchSaveAction(payload: FormSavePayload, options: FormSaveOptions): void {
    if (!this.store) {
      return;
    }

    if (options.storeContext === 'inbox') {
      import('../../../../rx-state/rx-photo-inbox').then(({ RxPhotoInboxAction }) => {
        this.store!.dispatch(new RxPhotoInboxAction.SubmitForm([payload]));
        if (options.status !== undefined && options.bulkIds) {
          const bulkStatusPayload = {
            ids: options.bulkIds,
            status: options.status,
          };
          this.store!.dispatch(new RxPhotoInboxAction.ChangeFormsStatusBulk(bulkStatusPayload));
        }
      });
    } else {
      import('../../../../rx-state/client-library').then(({ ClientLibraryStoreActions }) => {
        if (options.status !== undefined && options.bulkIds) {
          const bulkStatusPayload = {
            ids: options.bulkIds,
            status: options.status,
          };
          this.store!.dispatch(new ClientLibraryStoreActions.FillSign(payload, bulkStatusPayload));
        } else {
          this.store!.dispatch(new ClientLibraryStoreActions.FillSign(payload));
        }
      });
    }
  }

  submitFromPresenter(
    presenterComponent: any,
    formId: number,
    options: FormSaveOptions = {},
  ): Observable<any> {
    const formData = presenterComponent.getFormDataJson
      ? presenterComponent.getFormDataJson()
      : presenterComponent.getData();

    return this.saveForm(formId, formData, presenterComponent, options);
  }
}
